import React, { useState } from 'react';
import { AlertTriangle, Info, CheckCircle, XCircle, Filter, Search, Download, ChevronDown, ChevronUp } from 'lucide-react';

const InsuranceComparisonWireframe = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedChange, setSelectedChange] = useState(null);

  // Function to get document content based on selected change
  const getDocumentContent = (changeNumber) => {
    switch (changeNumber) {
      case 1: // Primary & Non-Contributory Missing
        return {
          title: "SECTION IV - COMMERCIAL GENERAL LIABILITY CONDITIONS",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                2. Duties In The Event Of Occurrence, Offense, Claim Or Suit
              </p>
              <p className="text-sm text-gray-700 mb-3">
                a. You must see to it that we are notified as soon as practicable...
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-gray-500 italic">[Missing provision]</span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">MISSING: CG 20 01 Endorsement</p>
                    <p className="text-xs text-red-600 mt-1">This creates coverage gaps and potential carrier disputes</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                b. If a claim is made or suit is brought against any insured...
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                2. Duties In The Event Of Occurrence, Offense, Claim Or Suit
              </p>
              <p className="text-sm text-gray-700 mb-3">
                a. You must see to it that we are notified as soon as practicable...
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        This insurance is primary and non-contributory to any other insurance available to any additional insured.
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">INCLUDED: CG 20 01 Endorsement</p>
                    <p className="text-xs text-green-600 mt-1">Ensures proper coverage priority and eliminates disputes</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                b. If a claim is made or suit is brought against any insured...
              </p>
            </div>
          )
        };
      case 2: // Additional Insured Form Restricted
        return {
          title: "SECTION IV - ADDITIONAL INSURED ENDORSEMENTS",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                CG 20 10 (04/13) - Additional Insured - Designated Person or Organization
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-red-600 font-medium">
                        Coverage limited to work directly performed by the named insured. Excludes additional insured's own negligence.
                      </span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">RESTRICTIVE FORM</p>
                    <p className="text-xs text-red-600 mt-1">Limits coverage scope and may exclude negligence claims</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This endorsement modifies insurance provided under the following coverages...
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                CG 20 10 (11/85) - Additional Insured - Designated Person or Organization
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        Coverage includes completed operations and extends to additional insured's sole negligence in many jurisdictions.
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">BROAD FORM</p>
                    <p className="text-xs text-green-600 mt-1">Provides comprehensive coverage including completed operations</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This endorsement modifies insurance provided under the following coverages...
              </p>
            </div>
          )
        };
      case 3: // Aggregate Limit Reduced
        return {
          title: "SECTION I - DECLARATIONS - LIMITS OF INSURANCE",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                COMMERCIAL GENERAL LIABILITY COVERAGE
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-red-600 font-medium">
                        Each Occurrence: $1,000,000
                      </span>
                    </p>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-red-600 font-medium">
                        General Aggregate: $2,000,000
                      </span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">BELOW CONTRACT MINIMUM</p>
                    <p className="text-xs text-red-600 mt-1">Contract requires $2M per occurrence, $4M aggregate</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                Products-Completed Operations Aggregate: $2,000,000
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                COMMERCIAL GENERAL LIABILITY COVERAGE
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        Each Occurrence: $2,000,000
                      </span>
                    </p>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        General Aggregate: $4,000,000
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">MEETS CONTRACT REQUIREMENTS</p>
                    <p className="text-xs text-green-600 mt-1">Adequate limits for construction project exposure</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                Products-Completed Operations Aggregate: $4,000,000
              </p>
            </div>
          )
        };
      case 4: // Completed Ops Coverage Period
        return {
          title: "SECTION III - COVERAGE - COMMERCIAL GENERAL LIABILITY",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Coverage A - Bodily Injury and Property Damage Liability
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-red-600 font-medium">
                        Completed Operations Coverage Period: 2 years from completion
                      </span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">INADEQUATE PERIOD</p>
                    <p className="text-xs text-red-600 mt-1">Defects often appear 3-5 years after completion</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This insurance applies to "bodily injury" and "property damage" only if...
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Coverage A - Bodily Injury and Property Damage Liability
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        Completed Operations Coverage Period: 5 years from completion
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">ADEQUATE PERIOD</p>
                    <p className="text-xs text-green-600 mt-1">Covers typical defect discovery timeline</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This insurance applies to "bodily injury" and "property damage" only if...
              </p>
            </div>
          )
        };
      case 5: // Deductible Increased
        return {
          title: "SECTION II - CONDITIONS - DEDUCTIBLE",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Deductible Provisions
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <Info className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-orange-600 font-medium">
                        Deductible Amount: $10,000 per occurrence
                      </span>
                    </p>
                    <p className="text-xs text-orange-600 font-semibold">HIGHER DEDUCTIBLE</p>
                    <p className="text-xs text-orange-600 mt-1">Increases out-of-pocket exposure per claim</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                The deductible applies to all damages because of "bodily injury" and "property damage"...
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Deductible Provisions
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        Deductible Amount: $5,000 per occurrence
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">LOWER DEDUCTIBLE</p>
                    <p className="text-xs text-green-600 mt-1">Reduces out-of-pocket exposure per claim</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                The deductible applies to all damages because of "bodily injury" and "property damage"...
              </p>
            </div>
          )
        };
      case 6: // Waiver of Subrogation Modified
        return {
          title: "SECTION IV - ENDORSEMENTS - WAIVER OF SUBROGATION",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                CG 24 26 - Waiver of Transfer of Rights of Recovery Against Others to Us
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        Blanket waiver for all projects and additional insureds
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">BLANKET WAIVER</p>
                    <p className="text-xs text-green-600 mt-1">Simplifies administration and ensures consistent protection</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                We waive any right of recovery we may have against the person or organization...
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                CG 24 26 - Waiver of Transfer of Rights of Recovery Against Others to Us
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <Info className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-orange-600 font-medium">
                        Project-specific waiver requiring individual endorsements
                      </span>
                    </p>
                    <p className="text-xs text-orange-600 font-semibold">PROJECT-SPECIFIC</p>
                    <p className="text-xs text-orange-600 mt-1">Increases administrative burden and potential for missed endorsements</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                We waive any right of recovery we may have against the person or organization...
              </p>
            </div>
          )
        };
      case 7: // Certificate Holder Name Change
        return {
          title: "DECLARATIONS - CERTIFICATE HOLDER INFORMATION",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Certificate Holder Details
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-blue-600 font-medium">
                        Certificate Holder: ABC Construction Company LLC
                      </span>
                    </p>
                    <p className="text-xs text-blue-600 font-semibold">PREVIOUS NAME</p>
                    <p className="text-xs text-blue-600 mt-1">Administrative change with no coverage impact</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                Address: 123 Main Street, City, State 12345
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Certificate Holder Details
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        Certificate Holder: ABC Construction Group Inc.
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">UPDATED NAME</p>
                    <p className="text-xs text-green-600 mt-1">Reflects current project owner and contract requirements</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                Address: 123 Main Street, City, State 12345
              </p>
            </div>
          )
        };
      default:
        return {
          title: "SECTION IV - COMMERCIAL GENERAL LIABILITY CONDITIONS",
          leftContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                2. Duties In The Event Of Occurrence, Offense, Claim Or Suit
              </p>
              <p className="text-sm text-gray-700 mb-3">
                a. You must see to it that we are notified as soon as practicable...
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-gray-500 italic">[Missing provision]</span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">MISSING: CG 20 01 Endorsement</p>
                    <p className="text-xs text-red-600 mt-1">This creates coverage gaps and potential carrier disputes</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                b. If a claim is made or suit is brought against any insured...
              </p>
            </div>
          ),
          rightContent: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                2. Duties In The Event Of Occurrence, Offense, Claim Or Suit
              </p>
              <p className="text-sm text-gray-700 mb-3">
                a. You must see to it that we are notified as soon as practicable...
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-green-700 font-medium">
                        This insurance is primary and non-contributory to any other insurance available to any additional insured.
                      </span>
                    </p>
                    <p className="text-xs text-green-600 font-semibold">INCLUDED: CG 20 01 Endorsement</p>
                    <p className="text-xs text-green-600 mt-1">Ensures proper coverage priority and eliminates disputes</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                b. If a claim is made or suit is brought against any insured...
              </p>
            </div>
          )
        };
    }
  };

  // Helper to provide explanation/impact/recommendation for the selected change
  const getChangeDetails = (changeNumber) => {
    switch (changeNumber) {
      case 1:
        return {
          explanation:
            'Policy A lacks the CG 20 01 endorsement that establishes this policy as primary and non-contributory to other available insurance.',
          impact:
            "Without this language, multiple carriers may dispute coverage responsibility, leading to expensive litigation and delayed claim payments. The additional insured may face defense costs while carriers fight over coverage.",
          recommendation:
            "Require CG 20 01 endorsement to ensure this policy responds first and doesn't contribute with other available insurance."
        };
      case 2:
        return {
          explanation:
            'Policy A uses the restrictive CG 20 10 (04/13) form which limits additional insured coverage to work directly performed by the named insured.',
          impact:
            "This excludes coverage for the additional insured's own negligence and may not include completed operations coverage, creating significant gaps in protection.",
          recommendation:
            'Request the broader CG 20 10 (11/85) form or negotiate specific language to include completed operations and negligence coverage.'
        };
      case 3:
        return {
          explanation:
            "Policy A's aggregate limit of $2M is below the typical contract requirement of $4M for construction projects.",
          impact:
            'If multiple claims occur during the policy period, coverage may be exhausted early, leaving the insured responsible for additional claims.',
          recommendation:
            'Increase aggregate limits to meet contract requirements or negotiate contract terms to accept lower limits.'
        };
      case 4:
        return {
          explanation:
            "Policy A's completed operations coverage ends after 2 years, which is shorter than the typical statute of repose period.",
          impact:
            'Construction defects often appear 3-5 years after completion. This creates a coverage gap where defects are discovered but no insurance exists.',
          recommendation:
            'Extend completed operations coverage to match the statute of repose period or purchase extended reporting period coverage.'
        };
      case 5:
        return {
          explanation:
            "Policy A's deductible of $10,000 is higher than Policy B's $5,000 deductible.",
          impact:
            'Higher deductibles reduce premium costs but increase out-of-pocket exposure for each claim, affecting cash flow and risk tolerance.',
          recommendation:
            'Evaluate cost-benefit trade-off between premium savings and increased deductible exposure based on claim frequency.'
        };
      case 6:
        return {
          explanation:
            'Policy A provides blanket waiver of subrogation while Policy B requires project-specific endorsements.',
          impact:
            'Project-specific waivers increase administrative burden and potential for missed endorsements, affecting risk transfer efficiency.',
          recommendation:
            'Consider blanket waiver endorsement for Policy B to reduce administrative complexity and ensure consistent protection.'
        };
      case 7:
        return {
          explanation:
            'The certificate holder name has been updated to reflect the current project owner.',
          impact:
            'This is primarily an administrative change with no coverage impact, but ensures proper certificate issuance.',
          recommendation:
            'Verify the new certificate holder name matches the contract requirements and update certificate requests accordingly.'
        };
      default:
        return {
          explanation: 'Select a change from the left panel to view details.',
          impact: 'Risk impact will appear here when a change is selected.',
          recommendation: ''
        };
    }
  };

  // Dashboard View
  const DashboardView = () => (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-lg p-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-700">47</div>
                    <div className="text-xs text-gray-500">/100</div>
                  </div>
                </div>
                <div className="absolute -top-2 -right-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full font-bold">
                  HIGH RISK
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-1">Overall Risk Score</h2>
                <p className="text-sm text-gray-600 mb-2">Policy A vs Policy B Comparison</p>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-red-500 h-2 rounded-full" style={{width: '47%'}}></div>
                  </div>
                  <span className="text-xs text-gray-600">47% Risk Level</span>
                </div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="bg-red-600 text-white px-6 py-4 rounded-xl font-bold text-3xl mb-2 shadow-lg">
              3
            </div>
            <p className="text-sm text-gray-700 font-semibold">Critical Issues</p>
            <p className="text-xs text-gray-600 mt-1">Require Immediate Attention</p>
          </div>
        </div>
      </div>

      {/* Critical Issues Section */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-red-600 text-white px-3 py-2 rounded-lg font-bold text-lg">
              3
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Critical Issues Requiring Immediate Attention</h3>
              <p className="text-sm text-gray-600">These issues pose significant financial and legal risks</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <ChevronDown className="w-4 h-4" />
            <span>Click any issue to expand detailed analysis</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CriticalIssue
            priority="critical"
            title="Missing Primary & Non-Contributory Language"
            impact="$2M+ potential exposure in multi-party claims"
            explanation="Policy A lacks the CG 20 01 endorsement that establishes this policy as primary and non-contributory to other available insurance. Without this language, multiple carriers may dispute coverage responsibility, leading to expensive litigation and delayed claim payments."
            recommendation="Require CG 20 01 endorsement to ensure this policy responds first and doesn't contribute with other available insurance. This eliminates carrier disputes and ensures prompt claim resolution."
          />
          <CriticalIssue
            priority="critical"
            title="Restrictive Additional Insured Endorsement (CG 20 10 04/13)"
            impact="Limited coverage for GC negligence claims"
            explanation="Policy A uses the restrictive CG 20 10 (04/13) form which limits additional insured coverage to work directly performed by the named insured. This excludes coverage for the additional insured's own negligence and may not include completed operations coverage."
            recommendation="Request the broader CG 20 10 (11/85) form or negotiate specific language to include completed operations and negligence coverage. This ensures comprehensive protection for the general contractor."
          />
          <CriticalIssue
            priority="critical"
            title="Completed Operations Coverage Ends Before Statute of Repose"
            impact="No coverage for defects appearing after 2 years"
            explanation="Policy A's completed operations coverage ends after 2 years, which is shorter than the typical statute of repose period. Construction defects often appear 3-5 years after completion due to weather exposure, settling, or material degradation."
            recommendation="Extend completed operations coverage to match the statute of repose period (typically 4-10 years) or purchase extended reporting period coverage. This ensures coverage for defects discovered after project completion."
          />
        </div>
      </div>

      {/* Priority Distribution Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Priority Distribution</h3>
        <DonutChart 
          data={[
            { label: 'Critical Priority', value: 8, priority: 'critical' },
            { label: 'High Priority', value: 23, priority: 'high' },
            { label: 'Medium Priority', value: 41, priority: 'medium' },
            { label: 'Low Priority', value: 55, priority: 'low' }
          ]}
          size={180}
        />
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-600 mb-3">Compliance Status</h3>
          <div className="space-y-2">
            <ComplianceItem 
              status="pass" 
              text="General Liability Limits" 
              details="Policy A: $1M/$2M (below contract minimum), Policy B: $2M/$4M (meets requirements)"
            />
            <ComplianceItem 
              status="fail" 
              text="Additional Insured Coverage" 
              details="Policy A uses restrictive CG 20 10 (04/13) form, Policy B uses broader CG 20 10 (11/85) form"
            />
            <ComplianceItem 
              status="fail" 
              text="Primary & Non-Contributory" 
              details="Policy A missing CG 20 01 endorsement, Policy B includes proper endorsement"
            />
            <ComplianceItem 
              status="pass" 
              text="Workers' Compensation" 
              details="Both policies include adequate workers' compensation coverage as required"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-600 mb-3">Coverage Gaps</h3>
          <div className="space-y-3">
            <GapItem 
              amount="$2.0M" 
              text="Missing umbrella exposure" 
              impact="Potential uncovered claims above primary limits"
            />
            <GapItem 
              amount="$500K" 
              text="Completed operations gap" 
              impact="No coverage for defects after 2 years"
            />
            <GapItem 
              amount="$300K" 
              text="Deductible difference" 
              impact="Higher out-of-pocket exposure per claim"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-600 mb-3">Document Metadata</h3>
          <div className="space-y-2 text-sm">
            <MetadataRow label="Policy A" value="ABC-2024-12345" />
            <MetadataRow label="Policy B" value="XYZ-2024-67890" />
            <MetadataRow label="Effective" value="01/15/2025" />
            <MetadataRow label="Comparison Date" value="10/15/2025" />
          </div>
        </div>
      </div>
    </div>
  );

  // Side-by-Side Comparison View
  const ComparisonView = () => (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-600" />
              <select
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical Only</option>
                <option value="high">High & Above</option>
                <option value="medium">Medium & Above</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-600" />
              <input
                type="text"
                placeholder="Search provisions..."
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-64"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              <Download className="w-4 h-4" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* Main Comparison Area */}
      <div className="flex gap-4">
        {/* Changes Panel */}
        <div className="w-80 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" style={{maxHeight: '600px'}}>
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-bold text-gray-800">Changes (127)</h3>
            <p className="text-xs text-gray-600 mt-1">8 Critical • 23 High • 41 Medium • 55 Low</p>
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
              <ChevronDown className="w-3 h-3" />
              <span>Click any change to view in document</span>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            <ChangeItem
              priority="critical"
              title="Primary & Non-Contributory Missing"
              section="Section IV - Endorsements"
              number={1}
              isSelected={selectedChange === 1}
              onSelect={() => setSelectedChange(1)}
            />
            <ChangeItem
              priority="critical"
              title="Additional Insured Form Restricted"
              section="Section IV - Endorsements"
              number={2}
              isSelected={selectedChange === 2}
              onSelect={() => setSelectedChange(2)}
            />
            <ChangeItem
              priority="high"
              title="Aggregate Limit Reduced"
              section="Section I - Limits"
              number={3}
              isSelected={selectedChange === 3}
              onSelect={() => setSelectedChange(3)}
            />
            <ChangeItem
              priority="high"
              title="Completed Ops Coverage Period"
              section="Section III - Coverage"
              number={4}
              isSelected={selectedChange === 4}
              onSelect={() => setSelectedChange(4)}
            />
            <ChangeItem
              priority="medium"
              title="Deductible Increased"
              section="Section II - Conditions"
              number={5}
              isSelected={selectedChange === 5}
              onSelect={() => setSelectedChange(5)}
            />
            <ChangeItem
              priority="medium"
              title="Waiver of Subrogation Modified"
              section="Section IV - Endorsements"
              number={6}
              isSelected={selectedChange === 6}
              onSelect={() => setSelectedChange(6)}
            />
            <ChangeItem
              priority="low"
              title="Certificate Holder Name Change"
              section="Declarations"
              number={7}
              isSelected={selectedChange === 7}
              onSelect={() => setSelectedChange(7)}
            />
          </div>
        </div>

        {/* Document Comparison */}
        <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Analysis Header */}
          {selectedChange && (
            <div className="bg-gray-50 border-b border-gray-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-600" />
                    Explanation
                  </h5>
                  <p className="text-sm text-gray-700">{getChangeDetails(selectedChange).explanation}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Risk Impact
                  </h5>
                  <p className="text-sm text-gray-700">{getChangeDetails(selectedChange).impact}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex border-b border-gray-200">
            <div className="flex-1 p-3 bg-blue-50 border-r border-gray-200">
              <p className="font-semibold text-sm text-gray-800">Policy A - ABC Insurance</p>
              <p className="text-xs text-gray-600">Policy #: ABC-2024-12345</p>
            </div>
            <div className="flex-1 p-3 bg-green-50">
              <p className="font-semibold text-sm text-gray-800">Policy B - XYZ Insurance</p>
              <p className="text-xs text-gray-600">Policy #: XYZ-2024-67890</p>
            </div>
          </div>
          
          <div className="flex" style={{height: selectedChange ? '400px' : '500px'}}>
            {/* Left Document */}
            <div className="flex-1 p-4 overflow-y-auto border-r border-gray-200">
              <DocumentSection
                title={getDocumentContent(selectedChange).title}
                side="left"
                isHighlighted={selectedChange !== null}
              >
                {getDocumentContent(selectedChange).leftContent}
              </DocumentSection>
            </div>

            {/* Right Document */}
            <div className="flex-1 p-4 overflow-y-auto">
              <DocumentSection
                title={getDocumentContent(selectedChange).title}
                side="right"
                isHighlighted={selectedChange !== null}
              >
                {getDocumentContent(selectedChange).rightContent}
              </DocumentSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Matrix Comparison View
  const MatrixView = () => (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-gray-800">Coverage Comparison Matrix</h3>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <ChevronDown className="w-4 h-4" />
            <span>Click any row to expand detailed analysis</span>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left p-3 font-semibold text-gray-700">Coverage Element</th>
              <th className="text-left p-3 font-semibold text-gray-700">Policy A</th>
              <th className="text-left p-3 font-semibold text-gray-700">Policy B</th>
              <th className="text-left p-3 font-semibold text-gray-700">Priority</th>
              <th className="text-left p-3 font-semibold text-gray-700">Gap Analysis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <MatrixRow
              element="Additional Insured Form"
              policyA="CG 20 10 (04/13)"
              policyB="CG 20 10 (11/85)"
              priority="critical"
              gap="Restrictive form limits completed ops coverage"
              explanation="The CG 20 10 (04/13) form is highly restrictive and excludes coverage for the additional insured's own negligence. The older CG 20 10 (11/85) form provides broader coverage including completed operations."
              impact="This difference can mean millions in uncovered claims when defects appear after project completion. The restrictive form may leave the general contractor without coverage for their own negligence."
              recommendation="Request the broader CG 20 10 (11/85) form or negotiate specific language to include completed operations coverage."
            />
            <MatrixRow
              element="Primary & Non-Contributory"
              policyA="Not Included"
              policyB="CG 20 01 Included"
              priority="critical"
              gap="Missing endorsement creates exposure"
              explanation="Without primary & non-contributory language, multiple insurance carriers may dispute coverage responsibility, leading to expensive inter-carrier litigation."
              impact="The additional insured may be forced to pay defense costs while carriers fight over who should respond. This can delay claim payments by months or years."
              recommendation="Require CG 20 01 endorsement to ensure this policy responds first and doesn't contribute with other available insurance."
            />
            <MatrixRow
              element="General Liability Limit"
              policyA="$1M / $2M"
              policyB="$2M / $4M"
              priority="high"
              gap="Policy A below contract minimum of $2M"
              explanation="Most construction contracts require minimum $2M per occurrence limits. Policy A's $1M limit creates a $1M gap in coverage."
              impact="If a claim exceeds $1M, the insured must pay the difference out-of-pocket, potentially exposing them to significant financial risk."
              recommendation="Increase Policy A limits to meet contract requirements or negotiate contract terms to accept lower limits."
            />
            <MatrixRow
              element="Completed Operations Period"
              policyA="2 years"
              policyB="5 years"
              priority="high"
              gap="Coverage ends before statute of repose"
              explanation="Construction defects often appear 3-5 years after completion due to weather exposure, settling, or material degradation."
              impact="Policy A's 2-year limit leaves a 3-year gap where defects are discovered but no coverage exists. Most states have 4-10 year statutes of repose."
              recommendation="Extend completed operations coverage to match the statute of repose period or purchase extended reporting period coverage."
            />
            <MatrixRow
              element="Waiver of Subrogation"
              policyA="Blanket (all projects)"
              policyB="Project-specific only"
              priority="medium"
              gap="Policy B requires individual endorsements"
              explanation="Blanket waivers protect against the carrier seeking recovery from other parties for any project. Project-specific waivers require separate endorsements for each job."
              impact="This increases administrative burden and potential for missed endorsements, affecting risk transfer efficiency."
              recommendation="Consider blanket waiver endorsement for Policy B to reduce administrative complexity and ensure consistent protection."
            />
            <MatrixRow
              element="Deductible"
              policyA="$10,000"
              policyB="$5,000"
              priority="low"
              gap="$5K difference per occurrence"
              explanation="Higher deductibles reduce premium costs but increase out-of-pocket exposure for each claim."
              impact="The $5,000 difference means Policy A requires $5,000 more per claim before coverage kicks in, affecting cash flow and risk tolerance."
              recommendation="Evaluate cost-benefit trade-off between premium savings and increased deductible exposure based on claim frequency."
            />
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Construction Insurance Comparison Tool
          </h1>
          <p className="text-gray-600">
            Comprehensive analysis of Policy A vs Policy B • Generated Oct 15, 2025
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <TabButton
            active={activeView === 'dashboard'}
            onClick={() => setActiveView('dashboard')}
            label="Executive Dashboard"
          />
          <TabButton
            active={activeView === 'comparison'}
            onClick={() => setActiveView('comparison')}
            label="Side-by-Side Comparison"
          />
          <TabButton
            active={activeView === 'matrix'}
            onClick={() => setActiveView('matrix')}
            label="Coverage Matrix"
          />
        </div>

        {/* Active View */}
        {activeView === 'dashboard' && <DashboardView />}
        {activeView === 'comparison' && <ComparisonView />}
        {activeView === 'matrix' && <MatrixView />}
      </div>
    </div>
  );
};

// Helper Components
const TabButton = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 font-semibold text-sm transition-colors ${
      active
        ? 'text-blue-600 border-b-2 border-blue-600'
        : 'text-gray-600 hover:text-gray-800'
    }`}
  >
    {label}
  </button>
);

const DonutChart = ({ data, size = 200 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const radius = size / 2 - 20;
  const circumference = 2 * Math.PI * radius;
  let cumulativePercentage = 0;

  const colors = {
    critical: '#EF4444', // red-500
    high: '#F97316',     // orange-500
    medium: '#EAB308',   // yellow-500
    low: '#3B82F6'       // blue-500
  };

  return (
    <div className="flex items-center gap-6">
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="20"
          />
          {/* Data segments */}
          {data.map((item, index) => {
            const percentage = (item.value / total) * 100;
            const strokeDasharray = circumference;
            const strokeDashoffset = circumference - (percentage / 100) * circumference;
            
            const segment = (
              <circle
                key={item.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={colors[item.priority]}
                strokeWidth="20"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className="transition-all duration-300 hover:stroke-opacity-80"
                style={{
                  transformOrigin: `${size / 2}px ${size / 2}px`,
                  transform: `rotate(${cumulativePercentage * 3.6}deg)`
                }}
              />
            );
            cumulativePercentage += percentage;
            return segment;
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-800">{total}</div>
            <div className="text-xs text-gray-600">Total Changes</div>
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        {data.map((item) => {
          const percentage = ((item.value / total) * 100).toFixed(0);
          return (
            <div key={item.label} className="flex items-center gap-3">
              <div 
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: colors[item.priority] }}
              ></div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800">{item.value}</span>
                  <span className="text-xs text-gray-600">({percentage}%)</span>
                </div>
                <div className="text-xs text-gray-600">{item.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const ComplianceItem = ({ status, text, details }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="border-b border-gray-100 pb-2 last:border-b-0">
      <div 
        className="flex items-center gap-2 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {status === 'pass' ? (
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        ) : (
          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
        )}
        <span className="text-sm text-gray-700 flex-1">{text}</span>
        {details && (
          <div className="flex items-center gap-1">
            {isExpanded ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />}
          </div>
        )}
      </div>
      {isExpanded && details && (
        <div className="mt-2 ml-6 p-2 bg-gray-50 rounded text-xs text-gray-600">
          {details}
        </div>
      )}
    </div>
  );
};

const GapItem = ({ amount, text, impact }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-3 hover:bg-red-100 transition-colors">
    <div className="flex items-center gap-2 mb-2">
      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
      <p className="text-lg font-bold text-red-600">{amount}</p>
    </div>
    <p className="text-xs text-gray-700 mb-1">{text}</p>
    {impact && (
      <p className="text-xs text-red-600 font-medium">{impact}</p>
    )}
  </div>
);

const MetadataRow = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">{label}:</span>
    <span className="font-semibold text-gray-800">{value}</span>
  </div>
);

const CriticalIssue = ({ priority, title, impact, explanation, recommendation }) => {
  const priorityColors = {
    critical: 'bg-red-50 border-red-300',
    high: 'bg-orange-50 border-orange-300'
  };

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`border rounded-lg ${priorityColors[priority]}`}>
      <div 
        className="p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 text-sm mb-1">{title}</h4>
            <p className="text-xs text-gray-600">{impact}</p>
            {!isExpanded && (
              <p className="text-xs text-gray-500 mt-1">Click to expand detailed analysis</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="px-4 pb-4 bg-white bg-opacity-50">
          <div className="space-y-3">
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600" />
                Explanation
              </h5>
              <p className="text-sm text-gray-700">{explanation}</p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Recommendation
              </h5>
              <p className="text-sm text-gray-700">{recommendation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ChangeItem = ({ priority, title, section, number, isSelected, onSelect }) => {
  const priorityColors = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-blue-100 text-blue-800'
  };

  const priorityIcons = {
    critical: <AlertTriangle className="w-3 h-3" />,
    high: <AlertTriangle className="w-3 h-3" />,
    medium: <Info className="w-3 h-3" />,
    low: <Info className="w-3 h-3" />
  };

  const handleClick = () => {
    onSelect();
  };

  return (
    <div className={`border-b border-gray-200 hover:bg-gray-50 ${isSelected ? 'bg-blue-50 border-blue-200' : ''}`}>
      <div 
        className="p-3 cursor-pointer"
        onClick={handleClick}
      >
        <div className="flex items-start gap-2 mb-1">
          <span className={`px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 ${priorityColors[priority]}`}>
            {priorityIcons[priority]}
            {priority.toUpperCase()}
          </span>
        </div>
        <h4 className="text-sm font-semibold text-gray-800 mb-1">{title}</h4>
        <p className="text-xs text-gray-600">{section}</p>
        <p className="text-xs text-gray-500 mt-1">Click to view in document</p>
        {isSelected && (
          <div className="mt-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
            Selected - View highlighted in document
          </div>
        )}
      </div>
    </div>
  );
};

const DocumentSection = ({ title, side, children, isHighlighted }) => (
  <div className={isHighlighted ? 'bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg' : ''}>
    <h3 className="text-xs font-bold text-gray-600 uppercase mb-3">{title}</h3>
    {children}
  </div>
);

const MatrixRow = ({ element, policyA, policyB, priority, gap, explanation, impact, recommendation }) => {
  const priorityColors = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-blue-100 text-blue-800'
  };

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <td className="p-3 font-medium text-gray-800 flex items-center gap-2">
          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
          {element}
        </td>
        <td className="p-3 text-gray-700">{policyA}</td>
        <td className="p-3 text-gray-700">{policyB}</td>
        <td className="p-3">
          <span className={`px-2 py-1 rounded text-xs font-semibold ${priorityColors[priority]}`}>
            {priority.toUpperCase()}
          </span>
        </td>
        <td className="p-3 text-gray-600 text-sm">
          <div className="flex items-center gap-2">
            <span>{gap}</span>
            <span className="text-xs text-gray-500">Click to expand</span>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan="5" className="p-4">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-600" />
                    Explanation
                  </h4>
                  <p className="text-sm text-gray-700">{explanation}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Risk Impact
                  </h4>
                  <p className="text-sm text-gray-700">{impact}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Recommendation
                  </h4>
                  <p className="text-sm text-gray-700">{recommendation}</p>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default InsuranceComparisonWireframe;
